import React, { useState } from 'react';
import 'styles/Sidebar.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import AlexWatermark from '../../../assets/axel-watermark.png'

const Sidebar = ({ isOpen, toggleSidebar }) => {
    const navigation = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [agentsOpen, setAgentsOpen] = useState(false);
    const [agenciesOpen, setAgenciesOpen] = useState(false);
    const [adminsOpen, setAdminsOpen] = useState(false);
    const [clientsOpen, setClientsOpen] = useState(false);

    // Helper function to determine if a menu item is active
    const isActiveRoute = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    // Helper function to get menu item classes (keeping original styling)
    const getMenuItemClasses = (path) => {
        if (isActiveRoute(path)) {
            return 'text-left w-full block border-r-4 text-rose-900 border-rose-900';
        } else {
            return 'text-left w-full block border-r-4 hover:text-rose-900 border-transparent text-black';
        }
    };

    // Sub-menu items: highlight only when path exactly matches (so only current page is active)
    const getSubMenuItemClasses = (path) => {
        if (location.pathname === path) {
            return 'text-left w-full block border-r-4 text-rose-900 border-rose-900';
        }
        return 'text-left w-full block border-r-4 hover:text-rose-900 border-transparent text-black';
    };


    const handleLogout = async () => {
        setIsLoggingOut(true);
        
        try {
            await logout(); // This calls the auth context logout which calls the API
            navigation('/login');
        } catch (error) {
            // Even if API call fails, still log out locally and redirect
            navigation('/login');
        } finally {
            setIsLoggingOut(false);
        }
    };
    return (
        <div className={`sidebar ${isOpen ? 'open' : ''} shadow-2xl bg-contain bg-no-repeat bg-white`} style={{ backgroundImage: `url(${AlexWatermark})` }}>
            <button 
                className="close-btn absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-3xl font-bold text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-all duration-200 z-50 shadow-lg border border-gray-300" 
                onClick={toggleSidebar}
                title="Close sidebar"
                aria-label="Close sidebar"
            >
                ✕
            </button>
            <ul className='mt-48'>
                <li className='py-3 pl-9'><button onClick={() => navigation('/')} className={getMenuItemClasses('/')}>Marketplace</button></li>
                <li className='py-3 pl-9'><button onClick={() => navigation('/submissions')} className={getMenuItemClasses('/submissions')}>Submissions</button></li>
                <li className='py-3 pl-9'><button onClick={() => navigation('/dashboard')} className={getMenuItemClasses('/dashboard')}>Dashboard</button></li>
                <li className='py-3 pl-9'>
                    <button onClick={() => setAgenciesOpen(!agenciesOpen)} className="text-left w-full block border-r-4 hover:text-rose-900 border-transparent text-black" aria-expanded={agenciesOpen}>
                        Agencies {agenciesOpen ? '▾' : '▸'}
                    </button>
                    {agenciesOpen && (
                        <ul className='list-none sidebar-submenu'>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/agencies')} className={getSubMenuItemClasses('/agencies')}>View Agencies</button></li>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/agencies/add')} className={getSubMenuItemClasses('/agencies/add')}>Add Agency</button></li>
                        </ul>
                    )}
                </li>
                <li className='py-3 pl-9'>
                    <button onClick={() => setAgentsOpen(!agentsOpen)} className="text-left w-full block border-r-4 hover:text-rose-900 border-transparent text-black" aria-expanded={agentsOpen}>
                        Agents {agentsOpen ? '▾' : '▸'}
                    </button>
                    {agentsOpen && (
                        <ul className='list-none sidebar-submenu'>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/agents')} className={getSubMenuItemClasses('/agents')}>View Agents</button></li>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/agents/add')} className={getSubMenuItemClasses('/agents/add')}>Add Agent</button></li>
                        </ul>
                    )}
                </li>
                <li className='py-3 pl-9'>
                    <button onClick={() => setAdminsOpen(!adminsOpen)} className={getMenuItemClasses('/admins')} aria-expanded={adminsOpen}>
                        Admins {adminsOpen ? '▾' : '▸'}
                    </button>
                    {adminsOpen && (
                        <ul className='list-none sidebar-submenu'>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/admins')} className={getSubMenuItemClasses('/admins')}>View Admins</button></li>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/admins/add')} className={getSubMenuItemClasses('/admins/add')}>Add Admins</button></li>
                        </ul>
                    )}
                </li>
                <li className='py-3 pl-9'>
                    <button onClick={() => setClientsOpen(!clientsOpen)} className={getMenuItemClasses('/clients')} aria-expanded={clientsOpen}>
                        Clients {clientsOpen ? '▾' : '▸'}
                    </button>
                    {clientsOpen && (
                        <ul className='list-none sidebar-submenu'>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/clients')} className={getSubMenuItemClasses('/clients')}>View Clients</button></li>
                            <li className='py-2 pl-9'><button onClick={() => navigation('/clients/add')} className={getSubMenuItemClasses('/clients/add')}>Add Clients</button></li>
                        </ul>
                    )}
                </li>
                <li className='py-3 pl-9'><button onClick={() => navigation('/resources')} className={getMenuItemClasses('/resources')}>Resources</button></li>
                {/* <li className='py-3 pl-9'><button onClick={() => navigation('/reports')} className={getMenuItemClasses('/reports')}>Report</button></li> */}
                <li className='py-3 pl-9'>
                    <button 
                        onClick={handleLogout} 
                        disabled={isLoggingOut}
                        className={`text-left w-full block border-r-4 hover:text-rose-900 border-transparent text-black ${isLoggingOut ? 'opacity-50 cursor-wait' : ''}`}
                    >
                        {isLoggingOut ? 'Logging out...' : 'Logout'}
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default Sidebar;
